import { ApolloClient, gql, useApolloClient, useQuery } from '@apollo/client';
import { GraphQLSchema } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ExplorerBehavioralFlags } from 'src/app/graph/explorerPage/explorerBehavioralFlags';
import {
  graphqlDocumentToTypescriptTypes,
  typescriptTypesByUniqueName,
} from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { createAutocompleteSuggestion } from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import {
  updateAllEnvironmentVariableEditors,
  useEnvironmentVariables,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/useEnvironmentVariables';
import {
  LoadRegisteredOperationCollections,
  UsePreflightSandboxOperationCollectionFragment,
  usePreflightSandbox,
} from 'src/app/graph/explorerPage/hooks/useExplorerState/usePreflightSandbox/usePreflightSandbox';
import { getGraphIdentifierFromGraphRef } from 'src/app/graph/explorerPage/hooks/usePerGraphIdentifierLocalStorage';
import { GraphRef, parseGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useForceLightMonacoTheme } from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import { useThreeLayoutInfo } from 'src/components/common/ThreePaneLayout';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import {
  getObjectValuesAtPosition,
  getObjectValuesInDocument,
} from 'src/lib/javascriptHelpers';

import { ExplorerDocsTab } from './helpers/ExplorerDocsTab';
import { JSONObject } from './helpers/types';

async function getOperationCollection(
  operationId: string | undefined,
  apolloClient: ApolloClient<unknown>,
) {
  if (!operationId) return null;
  const response = await apolloClient.query({
    query: gql<
      GraphQLTypes.UseScriptModalAutocompleteCurrentOperation,
      GraphQLTypes.UseScriptModalAutocompleteCurrentOperationVariables
    >`
      query UseScriptModalAutocompleteCurrentOperation(
        $collectionEntryIds: [ID!]!
      ) {
        operationCollectionEntries(collectionEntryIds: $collectionEntryIds) {
          id
          collection {
            id
            isSandbox
            isShared
            variants {
              id
            }
          }
        }
      }
    `,
    variables: { collectionEntryIds: [operationId] },
  });
  return response?.data.operationCollectionEntries[0]?.collection;
}
async function getGraphRefsForCurrentScope(
  operationId: string | undefined,
  graphRef: null | GraphRef,
  apolloClient: ApolloClient<unknown>,
): Promise<GraphQLTypes.UseScriptModalStateGraphVariants_service_variants[]> {
  const collection = await getOperationCollection(operationId, apolloClient);
  const ref = collection?.variants[0]?.id;
  const isSandbox = collection?.isSandbox || (!ref && graphRef === null);

  if (isSandbox) {
    const allGraphsResult = await apolloClient.query({
      query: gql<
        GraphQLTypes.UseScriptModalStateAllGraphs,
        GraphQLTypes.UseScriptModalStateAllGraphsVariables
      >`
        query UseScriptModalStateAllGraphs {
          me {
            id
            ... on User {
              memberships {
                user {
                  id
                }
                account {
                  id
                  graphs {
                    id
                    variants {
                      id
                      operationCollections {
                        id
                        isSandbox
                        isShared
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `,
    });
    const me =
      allGraphsResult?.data?.me?.__typename === 'User'
        ? allGraphsResult.data.me
        : null;
    return (
      me?.memberships.flatMap((membership) =>
        membership.account.graphs.flatMap((graph) => graph.variants),
      ) ?? []
    );
  } else if (graphRef) {
    const allGraphsResult = await apolloClient.query({
      query: gql<
        GraphQLTypes.UseScriptModalStateGraphVariants,
        GraphQLTypes.UseScriptModalStateGraphVariantsVariables
      >`
        query UseScriptModalStateGraphVariants($graphId: ID!) {
          service(id: $graphId) {
            id
            variants {
              id
              operationCollections {
                id
                isSandbox
                isShared
              }
            }
          }
        }
      `,
      variables: { graphId: graphRef.graphId },
    });
    return allGraphsResult.data.service?.variants ?? [];
  } else {
    return [];
  }
}

function getCollectionsInScope(
  values: { [key in 'scope' | 'graphRef']?: string | null },
  sandboxCollections:
    | GraphQLTypes.UsePreflightSandboxOperationCollectionFragment[]
    | undefined,
  graphRefResults: GraphQLTypes.LoadRegisteredOperationCollections[],
): GraphQLTypes.UsePreflightSandboxOperationCollectionFragment[] | null {
  const { graphRef, scope } = values;
  switch (scope) {
    case 'sandbox':
      return sandboxCollections?.filter((c) => c.operations.length > 0) ?? null;
    case 'personal':
    case 'shared':
    case undefined: {
      return graphRefResults
        .filter(({ service }) => !graphRef || service?.variant?.id === graphRef)
        .flatMap(
          (graph) =>
            graph.service?.variant?.operationCollections.filter(
              (c) =>
                c.operations.length > 0 &&
                (scope === 'personal'
                  ? !c.isShared && !c.isSandbox
                  : scope === 'shared'
                    ? c.isShared
                    : !scope
                      ? true
                      : assertUnreachable(scope)),
            ) ?? [],
        );
    }
    default:
      return null;
  }
}

function useRunOperationCompletion({
  graphRef,
  editor,
  graphqlSchema,
  currentOperationId,
}: {
  graphRef: GraphRef | null;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  graphqlSchema: GraphQLSchema;
  currentOperationId: string | undefined;
}) {
  const apolloClient = useApolloClient();

  // Get all current graph refs in the editor
  const lastSuccessfulDetectedRunOperationArgumentsRef = useRef<
    Record<
      'graphRefs' | 'collectionNames' | 'operationNames',
      (string | null)[]
    >
  >({ graphRefs: [], collectionNames: [], operationNames: [] });
  const detectedRunOperationArguments = useDerivedEditorValue({
    editor,
    getValue: useCallback((value: string) => {
      try {
        const { usages } = getObjectValuesInDocument(
          `async () => { "use strict"; ${value}; }`,
          ['graphRef', 'collectionName', 'operationName'],
        );
        lastSuccessfulDetectedRunOperationArgumentsRef.current = usages;
        return usages;
      } catch {
        return lastSuccessfulDetectedRunOperationArgumentsRef.current;
      }
    }, []),
    withPosition: false,
  });

  const [graphRefResults, setGraphRefResults] = useState<
    GraphQLTypes.LoadRegisteredOperationCollections[]
  >([]);
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      detectedRunOperationArguments.graphRefs.flatMap((ref) => {
        if (!ref) return [];
        const refObject = parseGraphRef(ref);
        if (refObject) {
          return apolloClient
            .query({
              query: LoadRegisteredOperationCollections,
              variables: refObject,
            })
            .then(({ data }) => (data.service?.variant ? data : null))
            .catch(() => null);
        } else {
          return [];
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setGraphRefResults(
        results.filter((r): r is NonNullable<typeof r> => !!r),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [detectedRunOperationArguments.graphRefs, apolloClient]);

  const sandboxCollectionsResult = useQuery(gql<
    GraphQLTypes.UseScriptModalSandboxCompletion,
    GraphQLTypes.UseScriptModalSandboxCompletionVariables
  >`
    query UseScriptModalSandboxCompletion {
      me {
        id
        ... on User {
          sandboxOperationCollections {
            id
            ...UsePreflightSandboxOperationCollectionFragment
          }
        }
      }
    }
    ${UsePreflightSandboxOperationCollectionFragment}
  `);
  const sandboxCollections = useMemo(() => {
    const me =
      sandboxCollectionsResult.data?.me?.__typename === 'User'
        ? sandboxCollectionsResult.data.me
        : null;
    return me?.sandboxOperationCollections;
  }, [sandboxCollectionsResult.data?.me]);

  const activeOperationIds = useMemo(() => {
    return [
      ...(sandboxCollections ?? []),
      ...graphRefResults.flatMap(
        (graph) => graph.service?.variant?.operationCollections ?? [],
      ),
    ]
      .filter((collection) =>
        detectedRunOperationArguments.collectionNames.includes(collection.name),
      )
      .flatMap((collection) =>
        collection.operations
          .filter((operation) =>
            detectedRunOperationArguments.operationNames.includes(
              operation.name,
            ),
          )
          .map((operation) => operation.id),
      );
  }, [detectedRunOperationArguments, graphRefResults, sandboxCollections]);
  const fetchedOperations = useQuery(
    gql<
      GraphQLTypes.PreflightScriptCompletionCollectionQuery,
      GraphQLTypes.PreflightScriptCompletionCollectionQueryVariables
    >`
      query PreflightScriptCompletionCollectionQuery(
        $collectionEntryIds: [ID!]!
      ) {
        operationCollectionEntries(collectionEntryIds: $collectionEntryIds) {
          id
          name
          collection {
            id
            name
            isShared
            isSandbox
            variants {
              id
            }
          }
          currentOperationRevision {
            body
          }
        }
      }
    `,
    {
      variables: { collectionEntryIds: activeOperationIds },
      skip: activeOperationIds.length === 0,
    },
  );

  const [commonCollectionTypes, setCommonCollectionTypes] = useState('');
  useEffect(() => {
    let cancelled = false;
    graphqlDocumentToTypescriptTypes(
      'query { __typename }',
      graphqlSchema,
      true,
      '',
    ).then((v) => {
      if (!cancelled) {
        setCommonCollectionTypes(v);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [graphqlSchema]);

  const [collectionTypes, setCollectionTypes] = useState('');
  useEffect(() => {
    let cancelled = false;
    Promise.allSettled(
      fetchedOperations.data?.operationCollectionEntries.map((entry) => {
        return graphqlDocumentToTypescriptTypes(
          entry.currentOperationRevision.body,
          graphqlSchema,
          false,
          entry.id,
        );
      }) ?? [],
    ).then((outputs) => {
      if (!cancelled) {
        setCollectionTypes(
          outputs
            .map((o) => (o.status === 'fulfilled' ? o.value : ''))
            .join('\n'),
        );
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fetchedOperations.data?.operationCollectionEntries, graphqlSchema]);

  useEffect(() => {
    const disposable = monacoEditor.languages.registerCompletionItemProvider(
      'javascript',
      {
        triggerCharacters: ["'", '"'],
        async provideCompletionItems(providerModel, position) {
          const { values, cursorKey } = getObjectValuesAtPosition(
            `async () => { "use strict"; ${providerModel.getValue()}; }`,
            ['scope', 'graphRef', 'collectionName', 'operationName'],
            position,
          );
          const currentOperationCollection = await getOperationCollection(
            currentOperationId,
            apolloClient,
          );

          if (
            cursorKey !== 'scope' &&
            currentOperationCollection?.isShared &&
            values.scope &&
            values.scope !== 'shared'
          ) {
            return undefined; // shared collections can only call shared collections, lets not suggest anything here
          }
          switch (cursorKey) {
            case 'scope': {
              return createAutocompleteSuggestion(
                providerModel,
                position,
                (values.graphRef === null
                  ? ['sandbox']
                  : typeof values.graphRef === 'string'
                    ? ['personal', 'shared']
                    : !values.graphRef
                      ? ['personal', 'shared', 'sandbox']
                      : assertUnreachable(values.graphRef)
                ).filter(
                  (scope) =>
                    scope === 'shared' || !currentOperationCollection?.isShared,
                ),
              );
            }
            case 'graphRef': {
              // graphRef options changed based on scope
              if (values.scope === 'sandbox') {
                return undefined; // graphRef isn't required if scope is sandbox
              } else {
                const graphsForScope = await getGraphRefsForCurrentScope(
                  currentOperationId,
                  graphRef,
                  apolloClient,
                );
                return createAutocompleteSuggestion(
                  providerModel,
                  position,
                  graphsForScope
                    .filter((v) =>
                      v.operationCollections.some((c) =>
                        values.scope === 'shared'
                          ? c.isShared
                          : values.scope === 'sandbox'
                            ? c.isSandbox
                            : values.scope === 'personal'
                              ? !c.isSandbox && !c.isShared
                              : true,
                      ),
                    )
                    .map((v) => v.id),
                );
              }
            }
            case 'collectionName':
            case 'operationName': {
              // collectionName and operationName require at least of of scope or graphRef to narrow down what to complete
              if (!values.scope && values.graphRef === undefined) {
                return undefined;
              }

              const collectionsToComplete = getCollectionsInScope(
                values,
                sandboxCollections,
                graphRefResults,
              );

              if (!collectionsToComplete) return undefined;
              switch (cursorKey) {
                case 'collectionName':
                  return createAutocompleteSuggestion(
                    providerModel,
                    position,
                    collectionsToComplete.map((c) => c.name),
                  );

                case 'operationName':
                  return createAutocompleteSuggestion(
                    providerModel,
                    position,
                    collectionsToComplete
                      .filter(
                        (c) =>
                          !values.collectionName ||
                          values.collectionName === c.name,
                      )
                      .flatMap((c) => c.operations.map((o) => o.name)),
                  );
                default:
                  return assertUnreachable(cursorKey);
              }
            }
            default:
          }
        },
      },
    );
    return () => disposable.dispose();
  }, [
    apolloClient,
    currentOperationId,
    graphRef,
    graphRefResults,
    sandboxCollections,
  ]);

  useEffect(() => {
    const disposable =
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        `${commonCollectionTypes}\n${collectionTypes}`,
      );
    return () => disposable.dispose();
  }, [collectionTypes, commonCollectionTypes]);
  return useMemo(() => {
    const operationIdLookup =
      fetchedOperations.data?.operationCollectionEntries.reduce(
        (
          acc: {
            [Scope in 'personal' | 'shared' | 'sandbox']?: {
              [GraphRef: string]: {
                [CollectionName: string]: { [OperationName: string]: string };
              };
            };
          },
          entry,
        ) => {
          const scope = entry.collection.isShared
            ? 'shared'
            : entry.collection.isSandbox
              ? 'sandbox'
              : 'personal';
          const scopeLookup = getOrSetRecord(scope, {}, acc);
          const variants =
            scope === 'sandbox' ? [{ id: 'null' }] : entry.collection.variants;
          for (const variant of variants) {
            const graphLookup = getOrSetRecord(variant.id, {}, scopeLookup);
            const collectionLookup = getOrSetRecord(
              entry.collection.name,
              {},
              graphLookup,
            );
            collectionLookup[entry.name] = entry.id;
          }
          return acc;
        },
        {},
      );
    function operationGeneric(isVariables: boolean) {
      function perEntryGeneric<Value>(
        values: Record<string, Value>,
        extend: string,
        getPerValue: (value: Value) => string,
      ) {
        return `${Object.entries(values)
          .map(
            (value) =>
              `${extend} extends ${
                extend === 'GraphRef' && value[0] === 'null'
                  ? value[0]
                  : JSON.stringify(value[0])
              } ? ${getPerValue(value[1])} : `,
          )
          .join('')}JSONObject`;
      }
      return perEntryGeneric(operationIdLookup ?? {}, 'Scope', (graphLookup) =>
        perEntryGeneric(graphLookup, 'GraphRef', (collectionLookup) =>
          perEntryGeneric(
            collectionLookup,
            'CollectionName',
            (operationLookup) =>
              perEntryGeneric(operationLookup, 'OperationName', (operationId) =>
                typescriptTypesByUniqueName(operationId, isVariables),
              ),
          ),
        ),
      );
    }

    const runOperationTypes = `runOperation<
            Scope extends string = 'sandbox',
            GraphRef extends string | null = null,
            CollectionName extends string,
            OperationName extends string,
          >(params: {
              scope: Scope,
              graphRef: GraphRef
              headers?: Record<string, string>,
              variables?: ${operationGeneric(true)},
              collectionName: CollectionName,
              operationName: OperationName,
            }): Promise<{
          result: undefined | {
            errors?: ReadonlyArray<Error>; // TODO: should we bother typing these errors as graphql errors?
            data?: ${operationGeneric(false)};
            extensions?: { [key: string]: unknown };
          };
          code: number;
        }>`;
    return { runOperationTypes };
  }, [fetchedOperations.data?.operationCollectionEntries]);
}

export function useScriptModalState({
  graphRef,
  editor,
  initialScriptValue,
  onClose,
  endpointUrl,
  includeCookies,
  explorerBehavioralFlags,
  currentOperationId,
  isOperationScriptEnabled,
  setDocsTab,
  graphqlSchema,
}: {
  graphRef: GraphRef | null;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  initialScriptValue: string;
  onClose: () => void;
  endpointUrl: string | null;
  includeCookies: boolean;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
  currentOperationId: string | undefined;
  isOperationScriptEnabled: boolean;
  setDocsTab: (newDocsTab: ExplorerDocsTab) => void;
  graphqlSchema: GraphQLSchema;
}) {
  const model = useMemo(() => editor.getModel(), [editor]);
  const handleClose = useCallback(() => {
    // Because the scriptEditor value for operation script is global, we need to reset the model to
    // the initial state if the user clicks `close` so their current value in
    // the editor isn't preserved
    model?.setValue(initialScriptValue);
    updateAllEnvironmentVariableEditors();
    onClose();
  }, [initialScriptValue, model, onClose]);

  useForceLightMonacoTheme();
  const layoutInfo = useThreeLayoutInfo({
    leftOpenKey: 'preflightSnippetsOpen',
    leftPanelWidthKey: 'preflightSnippetWidth',
    rightPanelWidthKey: 'preflightOutputWidth',
    centerMinWidth: 300,
    leftMinWidth: 200,
    rightMinWidth: 200,
    dividerWidth: 0,
  });
  const [logs, setLogs] = useState<['log' | 'warn' | 'error', ...string[]][][]>(
    [],
  );
  const {
    setEnvironmentVariables,
    getEnvironmentVariables,
    environmentVariablesEditor,
  } = useEnvironmentVariables({
    localStorageGraphIdentifier: graphRef
      ? getGraphIdentifierFromGraphRef(graphRef)
      : null,
    compactEditor: true,
  });

  const { runOperationTypes } = useRunOperationCompletion({
    graphRef,
    editor,
    graphqlSchema,
    currentOperationId,
  });

  useEffect(() => {
    const monacoDisposables = [
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        `const explorer: Readonly<{
            readonly CryptoJS: typeof CryptoJS;
            request: {
                body: OperationRequestBody;
            };
            response: {
                body?: JSONObject | null;
            }
            environment: {
                get(key: string): Readonly<JSONValue | undefined>;
                set(key: string, value: unknown): void;
            };
            oauth2Request: (
              authUrl: string;
              queryParams?: Record<string, string>;
            ) => Promise<Record<string, string | string[] | null> | null>;
            ${runOperationTypes}
            fetch: (href: string, options?: RequestInit) => Promise<{
              json: () => JSONValue;
              code: number;
              body: string;
              headers: Record<string, string>
            }>;
            prompt: (msg: string, defaultResponse?: string) => Promise<string | null>;
        }>`,
      ),
    ];

    return () =>
      monacoDisposables.forEach((disposable) => disposable.dispose());
  }, [runOperationTypes]);

  useEffect(() => {
    monacoEditor.languages.typescript.javascriptDefaults.setCompilerOptions({
      allowNonTsExtensions: true,
      // 'module' and 'target' both default to ESNext if `setCompilerOptions` was never called,
      // but needs to be explicitly set back to that once `setCompilerOptions` is explicitly called
      // Top-level 'await' expressions are only allowed when the 'module' option is set to 'esnext' or 'system', and the 'target' option is set to 'es2017' or higher
      // Top-level 'await' without requiring `export {}` that does nothing needs target to be set to ESNext
      module: monacoEditor.languages.typescript.ModuleKind.ESNext,
      target: monacoEditor.languages.typescript.ScriptTarget.ESNext,
      // by default, every lib is pulled in, including `lib.dom`, which includes a bunch of stuff we intentionally hid from preflight scripts or just don't exist (e.g. `window`)
      lib: [
        'es2015',
        'es2016',
        'es2017',
        'es2018',
        'es2019',
        'es2020',
        'es2021',
      ],
    });

    const monacoDisposables = [
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        // eslint-disable-next-line import/no-webpack-loader-syntax, @typescript-eslint/no-require-imports
        require('!!raw-loader!@types/crypto-js/index.d.ts').default,
      ),
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        // eslint-disable-next-line import/no-webpack-loader-syntax, @typescript-eslint/no-require-imports
        require('!!raw-loader!@types/node/console.d.ts').default,
      ),
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        `declare type JSONPrimitive = boolean | null | string | number;
        declare type JSONObject = { [key in string]?: JSONValue };
        declare type JSONValue = JSONPrimitive | JSONValue[] | JSONObject;
        declare interface OperationRequestBody {
          query: string;
          variables: JSONObject | null;
          operationName: string | undefined;
        }`,
      ),
      monacoEditor.languages.typescript.javascriptDefaults.addExtraLib(
        `/**
          * @deprecated \`pm\` is deprecated, please use \`explorer\` instead
          */
        const pm: {
          /**
           * @deprecated \`pm.environment\` is deprecated, please use \`explorer.environment\` instead
           */
          environment: {
              /**
               * @deprecated \`pm.environment.get\` is deprecated, please use \`explorer.environment.get\` instead
               */
              get: (key: string) => any;
              /**
               * @deprecated \`pm.environment.set\` is deprecated, please use \`explorer.environment.set\` instead
               */
              set: (key: string, value: unknown) => any;
          };
          /**
           * @deprecated \`pm.sendRequest\` is deprecated, please use \`explorer.fetch\` instead
           */
          sendRequest: (args_0: {
            url: string;
          } & Pick<RequestInit, "body" | "headers" | "method">, args_1: (err: unknown | undefined, response?: {
              code: number;
              body: string;
              json: () => JSONObject;
          }) => void) => any
      }`,
      ),
    ];

    return () =>
      monacoDisposables.forEach((disposable) => disposable.dispose());
  }, []);

  const logRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    logRef.current?.scrollTo(0, logRef.current.scrollHeight);
  }, [logs]);

  const runPreflightScript = usePreflightSandbox({
    endpointUrl,
    includeCookies,
    explorerBehavioralFlags,
    graphRef,
    currentOperationId,
    setDocsTab,
    isOperationScriptEnabled,
    isInTestContext: true,
  });

  const [promptRequest, setPromptRequest] = useState<{
    message: string;
    defaultValue: string | undefined;
    onSubmit: (response: string | null) => void;
  } | null>(null);
  const [oauth2Request, setOAuth2Request] = useState<{
    authUrl: string;
    queryParams: Record<string, string>;
    onComplete: (
      response: Record<string, string | string[] | null> | null,
    ) => void;
  } | null>(null);

  const activeRunIdRef = useRef(0);
  const [scriptRunning, setScriptRunning] = useState(false);
  const testRunScript = useCallback(
    async (mockResponseBody?: JSONObject | null) => {
      const runId = activeRunIdRef.current + 1;
      activeRunIdRef.current = runId;
      setScriptRunning(true);
      setLogs((allLogs) => [[], ...allLogs]);
      try {
        const result = await runPreflightScript({
          isTest: true,
          scriptType: 'preflight',
          script: editor.getValue(),
          environmentVariables: getEnvironmentVariables(),
          handlePrompt: (message, defaultValue) => {
            return new Promise((resolve) => {
              if (activeRunIdRef.current === runId) {
                setPromptRequest({
                  message,
                  defaultValue,
                  onSubmit(response) {
                    resolve(response);
                    setPromptRequest(null);
                  },
                });
              } else {
                resolve(null);
              }
            });
          },
          handleOAuth2Request: (authUrl, queryParams) =>
            new Promise((resolve) => {
              setOAuth2Request({
                authUrl,
                queryParams,
                onComplete(responseQueryParams) {
                  resolve(responseQueryParams);
                  setOAuth2Request(null);
                },
              });
            }),
          handleLogs: (level, ...msgs) => {
            if (activeRunIdRef.current === runId) {
              setLogs(([currentLogs, ...rest]) => [
                [...(currentLogs ?? []), [level, ...msgs]],
                ...rest,
              ]);
            }
          },
          requestBody: { query: '', variables: null, operationName: undefined },
          responseBody: mockResponseBody,
        });
        if (activeRunIdRef.current !== runId) return;
        setEnvironmentVariables(result);
      } catch (err) {
        if (activeRunIdRef.current !== runId) return;
        setLogs(([currentLogs, ...rest]) => [
          [...(currentLogs ?? []), ['error', String(err)]],
          ...rest,
        ]);
      }
      setScriptRunning(false);
    },
    [
      editor,
      getEnvironmentVariables,
      runPreflightScript,
      setEnvironmentVariables,
    ],
  );

  return {
    model,
    logRef,
    layoutInfo,
    logs,
    environmentVariablesEditor,
    testRunScript,
    scriptRunning,
    oauth2Request,
    promptRequest,
    handleClose,
  };
}
